import React, { useEffect, useCallback } from 'react';
import './richEditor.scss';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $convertToMarkdownString, $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, $getRoot, $createParagraphNode, EditorState, $createTextNode } from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW, KEY_TAB_COMMAND } from 'lexical';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TextBold16Filled, TextItalic16Filled, TextBulletListLtr16Regular, TextNumberListLtr16Regular, Link16Regular } from '@fluentui/react-icons';
import { Popover, PopoverTrigger, PopoverSurface, Field, Button, Input, OnOpenChangeData, InputOnChangeData, Link, Tooltip } from '@fluentui/react-components';

interface IMyEditorProps {
    onPropertyChanged: (value: string) => void;
    placeholder?: string;
    propertyType: string;
    initialValue?: string;
    draftIsMount: boolean;
}

const theme = {
    text: {
        bold: 'richEditor__bold',
        italic: 'richEditor__italic',
    },
    list: {
        ul: 'richEditor__ul',
        ol: 'richEditor__ol',
        listitem: 'richEditor__listitem',
    },
    link: 'richEditor__link',
};

// Plugin to load initial markdown content
function InitialValuePlugin({ initialValue, draftIsMount }: { initialValue?: string; draftIsMount: boolean }) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        if (initialValue !== undefined) {
            editor.update(() => {
                $convertFromMarkdownString(initialValue, TRANSFORMERS);
            });
        }
    }, [draftIsMount]);
    return null;
}

// Plugin to emit markdown on change
function MarkdownOnChangePlugin({ onChange }: { onChange: (markdown: string) => void }) {
    const [editor] = useLexicalComposerContext();
    return (
        <OnChangePlugin
            onChange={(editorState: EditorState) => {
                editorState.read(() => {
                    const markdown = $convertToMarkdownString(TRANSFORMERS);
                    onChange(markdown);
                });
            }}
        />
    );
}

// Toolbar plugin
function ToolbarPlugin({ propertyType }: { propertyType: string }) {
    const [editor] = useLexicalComposerContext();
    const [isLinkPopoverOpen, setIsLinkPopoverOpen] = React.useState(false);
    const [linkText, setLinkText] = React.useState('');
    const [linkAddress, setLinkAddress] = React.useState('');
    const [linkAddressErrorMessage, setLinkAddressErrorMessage] = React.useState('');

    const handleBold = (e: React.MouseEvent) => {
        e.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
    };

    const handleItalic = (e: React.MouseEvent) => {
        e.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
    };

    const handleBulletList = (e: React.MouseEvent) => {
        e.preventDefault();
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    };

    const handleOrderedList = (e: React.MouseEvent) => {
        e.preventDefault();
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    };

    const handleLinkPrompt = (e: React.MouseEvent) => {
        e.preventDefault();
        editor.getEditorState().read(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                setLinkText(selection.getTextContent());
            }
        });
        setIsLinkPopoverOpen(true);
    };

    const handleInsertLink = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!linkAddress || linkAddressErrorMessage) return;

        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const displayText = linkText || linkAddress;

            if (selection.isCollapsed()) {
                const textNode = $createTextNode(displayText);
                selection.insertNodes([textNode]);
                textNode.select(0, displayText.length);
            } else {
                if (linkText && linkText !== selection.getTextContent()) {
                    selection.insertText(linkText);
                    const newSelection = $getSelection();
                    if ($isRangeSelection(newSelection)) {
                        const anchor = newSelection.anchor.offset - linkText.length;
                        newSelection.anchor.offset = anchor;
                    }
                }
            }

            const updatedSelection = $getSelection();
            if ($isRangeSelection(updatedSelection)) {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: linkAddress, target: '_blank' });
            }
        });

        setIsLinkPopoverOpen(false);
        setLinkAddress('');
        setLinkText('');
        setLinkAddressErrorMessage('');
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsLinkPopoverOpen(false);
        setLinkAddress('');
        setLinkText('');
        setLinkAddressErrorMessage('');
    };

    const handleOpenChange = (e: any, data: OnOpenChangeData) => {
        if (!data.open) {
            setIsLinkPopoverOpen(false);
            setLinkAddress('');
            setLinkText('');
            setLinkAddressErrorMessage('');
        } else {
            setIsLinkPopoverOpen(true);
        }
    };

    const handleLinkAddressChange = (e: any, data: InputOnChangeData) => {
        const regex = /^(https:\/\/)([\da-z\.-]+\.[a-z\.]{2,6}|[\d\.]+)([\/:?=&#]{1}[\da-z\.%_+-]+)*[\/\?=]?$/;
        setLinkAddress(data.value);
        setLinkAddressErrorMessage(
            regex.test(data.value.toLowerCase()) ? '' : 'Only secure HTTPS URLs are allowed'
        );
    };

    return (
        <div className="richEditor__toolbar">
            <Button onMouseDown={handleBold} appearance='transparent' icon={<TextBold16Filled />} />
            <Button onMouseDown={handleItalic} appearance='transparent' icon={<TextItalic16Filled />} />
            {propertyType === 'Summary' && (
                <>
                    <Button onMouseDown={handleBulletList} appearance='transparent' icon={<TextBulletListLtr16Regular />} />
                    <Button onMouseDown={handleOrderedList} appearance='transparent' icon={<TextNumberListLtr16Regular />} />
                    <Popover trapFocus open={isLinkPopoverOpen} onOpenChange={handleOpenChange}>
                        <PopoverTrigger disableButtonEnhancement>
                            <Tooltip content="Insert a link" relationship="label">
                                <Button onClick={handleLinkPrompt} appearance='transparent' icon={<Link16Regular />} />
                            </Tooltip>
                        </PopoverTrigger>
                        <PopoverSurface aria-labelledby='linkPopover'>
                            <div className='richEditor__popover'>
                                <h4>Insert link</h4>
                                <Field label="Text to display">
                                    <Input
                                        className="inputField"
                                        value={linkText}
                                        onChange={(_, data) => setLinkText(data.value)}
                                        autoComplete="off"
                                    />
                                </Field>
                                <Field
                                    label="Address"
                                    required
                                    validationState={linkAddressErrorMessage ? 'error' : 'none'}
                                    validationMessage={linkAddressErrorMessage}
                                >
                                    <Input
                                        className="inputField"
                                        value={linkAddress}
                                        onChange={handleLinkAddressChange}
                                        autoComplete="off"
                                        required
                                    />
                                </Field>
                                <div className='richEditor__buttonContainer'>
                                    <Button onClick={handleCancel}>Cancel</Button>
                                    <Button
                                        onClick={handleInsertLink}
                                        disabled={!linkAddress || !!linkAddressErrorMessage}
                                        appearance='primary'
                                    >
                                        Insert
                                    </Button>
                                </div>
                            </div>
                        </PopoverSurface>
                    </Popover>
                </>
            )}
        </div>
    );
}

const MyEditor: React.FC<IMyEditorProps> = ({ onPropertyChanged, placeholder, propertyType, initialValue, draftIsMount }) => {
    const initialConfig = {
        namespace: 'MyEditor',
        theme,
        onError: (error: Error) => console.error(error),
        nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="richEditor">
                <ToolbarPlugin propertyType={propertyType} />
                <div className={propertyType === 'Author' ? 'richEditor__inputArea-author' : 'richEditor__inputArea'}>
                    <RichTextPlugin
                        contentEditable={<ContentEditable className="richEditor__contentEditable" />}
                        placeholder={<div className="richEditor__placeholder">{placeholder || ''}</div>}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </div>
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <InitialValuePlugin initialValue={initialValue} draftIsMount={draftIsMount} />
                <MarkdownOnChangePlugin onChange={onPropertyChanged} />
            </div>
        </LexicalComposer>
    );
};

export default MyEditor;